'use client';

import {
  App as AntdApp,
  ConfigProvider,
  theme as antdTheme,
  type ThemeConfig,
} from 'antd';
import { domAnimation, LazyMotion, motion } from 'framer-motion';
import mixpanel from 'mixpanel-browser';
import type { AppProps } from 'next/app';
import { Montserrat } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import NProgress from 'nprogress';
import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { RecoilRoot } from 'recoil';

import 'react-toastify/dist/ReactToastify.css';
import '../styles/globals.scss';
import '../styles/square.scss';
import '../styles/toolTip.scss';
import '../styles/ckEditor.scss';
import 'plyr/dist/plyr.css';
import '../styles/youtubeStyle.scss';
import '../styles/premium-modal.scss';
import '../styles/pdfReader.scss';
import '../styles/comingSoonBook.scss';
import 'nprogress/nprogress.css';
import '../styles/nprogress.scss';
import '../styles/video.scss';

import RenderIf from '@/components/Common/RenderIf';
import TrackingStorageManager from '@/components/LocalStorage/TrackingStorageManager';
import { initTokenRefresher } from '@/components/shared/ManageTokenForClient/tokenRefresher';

import { startEventProcessor } from '@/services/Events/eventBulkProcessor';
import { logFrontendError } from '@/services/globalErrorLog';
import GlobalCountrySync from '@/utils/GlobalCountrySync';
import { removeTrailingSlash } from '@/utils/removeTrailingSlash';

import Custom503 from './503';
import Layout from '../components/Layout';
import HydrationSafe from '../components/shared/HydrationSafe';
import CustomLoader from '../components/shared/Loading';
import PageTransition from '../components/shared/PageTransition';
import SmoothScroll from '../components/shared/SmoothScroll';
import { generateBreadcrumbSchema } from '../utils/seoSchemaBuilder';

const montserrat = Montserrat({
  subsets: ['latin'],
  fallback: ['Segoe UI', 'Roboto', 'Arial', 'sans-serif'],
  display: 'swap',
});

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

const config: ThemeConfig = {
  token: { colorPrimary: '#FF5612', fontFamily: 'inherit' },
};

export default function App({ Component, pageProps }: AppProps) {
  const { pathname, route } = useRouter();
  let previousPathname = pathname;
  const environment = process.env.NEXT_PUBLIC_APP_ENV || '';
  const maintenanceMode = process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true';
  const router = useRouter();
  const breadcrumbSchema = generateBreadcrumbSchema(router.asPath);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    initTokenRefresher();
  }, []);

  // Handle route change loading states
  useEffect(() => {
    NProgress.configure({ showSpinner: false });

    const handleStart = () => {
      NProgress.start();
      setIsLoading(true);
    };

    const handleComplete = () => {
      NProgress.done();
      setIsLoading(false);
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleComplete);
    };
  }, [router]);

  const pagesWithoutLayout = [
    '/pdfReader/[book_id]',
    '/pdfReader/[book_id]/[without_email]',
    '/order-successful',
  ];
  const pagesWithoutRouteAnimation = ['/books/[bookId]'];
  const excludeLayout = pagesWithoutLayout.includes(pathname);
  const excludeRouteAnimation = pagesWithoutRouteAnimation.includes(pathname);

  useEffect(() => {
    if (typeof window !== 'undefined' && MIXPANEL_TOKEN) {
      mixpanel.init(MIXPANEL_TOKEN, { debug: true });
    }
  }, []);

  const trackPageView = (url: string, pageTitle: string) => {
    if (typeof window === 'undefined') return;
    mixpanel.track(pageTitle, {
      url: url,
      pageTitle: pageTitle,
    });
  };

  useEffect(() => {
    if (!MIXPANEL_TOKEN || environment !== 'production') return;

    const handleRouteChange = (url: string) => {
      if (typeof window !== 'undefined' && previousPathname === pathname) {
        const pageTitle = document.title;
        trackPageView(url, pageTitle);
        previousPathname = pathname;
      }
    };

    handleRouteChange(pathname);
  }, [pathname]);

  // useEffect(() => {
  //   const handleRouteChange = () => {
  //     if (typeof window !== 'undefined') {
  //       console.clear();
  //     }
  //   };

  //   router.events.on('routeChangeStart', handleRouteChange);

  //   return () => {
  //     router.events.off('routeChangeStart', handleRouteChange);
  //   };
  // }, [router.events]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const TAG_ID = process.env.NEXT_PUBLIC_GOOGLE_ID_TAG;
      (window as any).dataLayer = (window as any).dataLayer || [];

      const gtag = (...args: any[]): void => {
        (window as any).dataLayer.push(...args);
      };

      gtag('js', new Date());
      gtag('config', TAG_ID, { debug_mode: true });
      (window as any).gtag = gtag;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const ignoredPatterns = [/^Warning:/, /ResizeObserver/, /Uncaught/];
    const shouldIgnore = (msg: string) =>
      ignoredPatterns.some((pattern) => pattern.test(msg));

    const logError = (msg: string, extra?: Record<string, any>) => {
      if (shouldIgnore(msg)) return;

      window.posthog?.capture('js_error', { message: msg, ...extra });

      logFrontendError({
        level: 'error',
        message: msg,
        additionalInfo: extra,
        useLogGroup: true,
      }).catch((err) => console.warn('Failed to send error log:', err));
    };

    const handleJsError = (event: ErrorEvent) => {
      logError(event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    };
    window.addEventListener('error', handleJsError);

    const handlePromiseRejection = (event: PromiseRejectionEvent) => {
      logError((event.reason as any)?.message || String(event.reason), {
        stack: (event.reason as any)?.stack || '',
      });
    };
    window.addEventListener('unhandledrejection', handlePromiseRejection);

    const handleResourceError = (event: Event) => {
      const target = event.target as HTMLElement & {
        src?: string;
        href?: string;
      };
      if (
        target &&
        (target.tagName === 'SCRIPT' ||
          target.tagName === 'LINK' ||
          target.tagName === 'IMG')
      ) {
        logError('Resource failed to load', {
          tagName: target.tagName,
          src: target.src,
          href: target.href,
        });
      }
    };
    window.addEventListener('error', handleResourceError, true);

    return () => {
      window.removeEventListener('error', handleJsError);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
      window.removeEventListener('error', handleResourceError, true);
    };
  }, []);

  useEffect(() => {
    startEventProcessor();
  }, []);

  if (maintenanceMode)
    return (
      <div className={montserrat.className}>
        <ConfigProvider
          theme={{ ...config, algorithm: [antdTheme.darkAlgorithm] }}
        >
          <Custom503 />
        </ConfigProvider>
      </div>
    );

  // Create a simple loading state for server-side rendering
  const loadingContent = (
    <div className='w-full h-screen flex items-center justify-center'>
      <div className='text-center'>
        <div className='text-2xl font-semibold'>
          <CustomLoader />
        </div>
      </div>
    </div>
  );

  // Page transition loading overlay
  const loadingOverlay = isLoading && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center'
    >
      <CustomLoader />
    </motion.div>
  );

  const content = <Component {...pageProps} key={router.asPath} />;

  const robotsContent =
    process.env.NEXT_PUBLIC_APP_ENV !== 'production'
      ? 'noindex, nofollow'
      : 'index, follow';
  const canonicalUrl = `https://zebralearn.com${router.asPath.split('?')[0]}`;

  return (
    <>
      <Head>
        <meta
          name='keywords'
          content='Bestselling Books, Courses, Learning, School, ecommerce, Affiliate, marketing, Online learning, E-books, Education, Learning platform, Study materials, Digital library, Online courses, Educational resources, Self-paced learning, Interactive learning, Distance learning, E-learning, Skill development, Knowledge acquisition, Study guides, Learning materials, Academic resources, Online education, Study resources, Digital textbooks, Tutorial videos, Exam preparation, Test materials, Study aids, Reference materials, Audio books, Video lectures, Educational content, Learning modules, Study notes, Webinars, Interactive courses, Study platform, Certification courses, Online training, Skill enhancement, Academic courses, Professional development, Career advancement, Learning management system, Virtual classroom, Online workshops, Exam practice, Study skills, Academic support, Educational technology, Lifelong learning, Study habits, Course materials, Online tutorials, Learning community, Study groups, Practice tests, Online degrees, Course catalogs, Study strategies, Learning resources, Educational tools, Online seminars, Online study groups, Homework help, Study techniques, Digital learning, Educational courses, Online classrooms, Learning environment, Study assistance, Exam tips, Study motivation, Skill-based courses, Career skills, Online textbooks, Study plans, Online coaching, Study sessions, Study aids, Online quizzes, Academic success, Online support, Study schedule, Learning objectives, Exam readiness, Academic achievement, Learning outcomes, Course completion, Test preparation '
        />
        <meta name='robots' content={robotsContent} key='robotsId' />
        <meta name='googlebot' content={robotsContent} />
        <meta charSet='utf-8' />

        <meta name='viewport' content='width=device-width, initial-scale=1' />

        <link
          rel='canonical'
          href={removeTrailingSlash(canonicalUrl) || 'https://zebralearn.com'}
          key='canonical'
        />

        <meta property='og:site_name' content='ZebraLearn' key='og-site_name' />
        <meta property='og:title' content='ZebraLearn' key='og-title' />
        <meta
          property='og:description'
          content='Discover the best books and courses on finance, career growth, and more.'
          key='og-description'
        />
        <meta property='og:type' content='website' key='og-type' />
        <meta property='og:url' content='https://zebralearn.com' key='og-url' />
        <meta
          property='og:image'
          content='https://zebralearn-store.s3.ap-south-1.amazonaws.com/thubnail-creative.png'
          key='og-image'
        />
        <meta property='og:locale' content='en_IN' key='og-locale' />
        <meta
          property='og:image:type'
          content='image/webp'
          key='og-image-type-webp'
        />
        <meta
          property='og:image:type'
          content='image/png'
          key='og-image-type-png'
        />
        <meta property='og:image:width' content='1200' key='ogimagewidth' />
        <meta property='og:image:height' content='630' key='ogimageheight' />
        <meta
          name='google-site-verification'
          content={process.env.NEXT_PUBLIC_SITE_VERIFICATION}
        />

        {/* Twitter Cards (Default) */}
        <meta
          name='twitter:card'
          content='summary_large_image'
          key='twitter-card'
        />
        <meta name='twitter:site' content='@zebralearn' key='twitter-site' />
        <meta
          name='twitter:creator'
          content='@zebralearn'
          key='twitter-creator'
        />
        <meta name='twitter:title' content='ZebraLearn' key='twitter-title' />
        <meta
          name='twitter:description'
          content='Find expert-reviewed books and courses tailored to your learning needs.'
          key='twitter-description'
        />
        <meta
          name='twitter:image'
          content='https://zebralearn-store.s3.ap-south-1.amazonaws.com/OG+1.png?tr=f-png'
          key='twitter-image'
        />

        {/* Other Metadata */}
        <meta property='og:image:alt' content='ZebraLearn' key='og-image-alt' />
        <meta
          property='og:image:type'
          content='image/png'
          key='og-image-type'
        />
        <meta property='og:image:width' content='1200' key='og-image-width' />
        <meta property='og:image:height' content='630' key='og-image-height' />


      </Head>

      {process.env.NEXT_PUBLIC_APP_ENV === 'staging' && (
        <Script
          id='posthog-script'
          strategy='afterInteractive'
          dangerouslySetInnerHTML={{
            __html: `
              !function(t,e){var o,n,p,r;e.__SV||(window.posthog && window.posthog.__loaded)||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init Dr qr Ci Br Zr Pr capture calculateEventProperties Ur register register_once register_for_session unregister unregister_for_session Xr getFeatureFlag getFeatureFlagPayload getFeatureFlagResult isFeatureEnabled reloadFeatureFlags updateFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey displaySurvey cancelPendingSurvey canRenderSurvey canRenderSurveyAsync Jr identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset setIdentity clearIdentity get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException captureLog startExceptionAutocapture stopExceptionAutocapture loadToolbar get_property getSessionProperty Wr Hr createPersonProfile setInternalOrTestUser Gr Fr tn opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing get_explicit_consent_status is_capturing clear_opt_in_out_capturing $r debug ki Yr getPageViewId captureTraceFeedback captureTraceMetric Rr".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
              posthog.init('phc_u42wGDZJc1ehsATgNIyeB0pAkqZyfzS89DorI2Kt2ld', {
                  api_host: 'https://us.i.posthog.com',
                  defaults: '2026-01-30',
                  person_profiles: 'always',
              })
            `,
          }}
        />
      )}
      <RenderIf condition={process.env.NEXT_PUBLIC_APP_ENV === 'staging'}>
        <Script
          id="nitro-commerce-init"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: `
      (function(n,i,t,r,o) {
      var a,m;n['NitroObject']=o;n[o]=n[o]||function(){
      (n[o].q=n[o].q||[]).push(arguments)},n[o].l=1*new Date();n[o].h=r;a=i.createElement(t),
      m=i.getElementsByTagName(t)[0];a.async=1;a.src=r;m.parentNode.insertBefore(a,m)
      })(window,document,'script','https://x.nitrocommerce.ai/nitro.js','nitro');
      nitro('configure', '65b4bc5c-1c35-4722-89ca-060c7785e459');
    `,
          }}
        />
      </RenderIf>

      <Script
        src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
        strategy='beforeInteractive'
      />

      <Script
        id='breadcrumb-schema'
        type='application/ld+json'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <canvas
        id='coupon-confetti'
        className='fixed top-0 left-0 w-screen h-screen pointer-events-none z-[99999]'
      />
      <div className={montserrat.className}>
        {!isHydrated ? (
          loadingContent
        ) : (
          <SmoothScroll>
            <RecoilRoot>
              <TrackingStorageManager />
              <GlobalCountrySync />
              <ConfigProvider
                theme={{ ...config, algorithm: [antdTheme.darkAlgorithm] }}
              >
                <AntdApp>
                  <HydrationSafe>
                    <LazyMotion features={domAnimation}>
                      {loadingOverlay}
                      {excludeLayout ? (
                        <PageTransition route={route}>{content}</PageTransition>
                      ) : (
                        <Layout>
                          {excludeRouteAnimation ? (
                            content
                          ) : (
                            <PageTransition route={route}>
                              {content}
                            </PageTransition>
                          )}
                        </Layout>
                      )}
                    </LazyMotion>
                  </HydrationSafe>
                </AntdApp>
              </ConfigProvider>
              <ToastContainer autoClose={2000} theme='dark' />
            </RecoilRoot>
          </SmoothScroll>
        )}
      </div>
    </>
  );
}
